"use client";

import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: {
      translate: {
        TranslateElement: new (
          options: { pageLanguage: string; autoDisplay: boolean },
          containerId: string
        ) => void;
      };
    };
  }
}

const languages = [
  { code: "EN",    googleCode: "en",    label: "English",            flag: "🇺🇸" },
  { code: "ES",    googleCode: "es",    label: "Español",            flag: "🇪🇸" },
  { code: "FR",    googleCode: "fr",    label: "Français",           flag: "🇫🇷" },
  { code: "IT",    googleCode: "it",    label: "Italiano",           flag: "🇮🇹" },
  { code: "EL",    googleCode: "el",    label: "Ελληνικά",           flag: "🇬🇷" },
  { code: "NL",    googleCode: "nl",    label: "Nederlands",         flag: "🇳🇱" },
  { code: "DA",    googleCode: "da",    label: "Dansk",              flag: "🇩🇰" },
  { code: "FI",    googleCode: "fi",    label: "Suomi",              flag: "🇫🇮" },
  { code: "DE",    googleCode: "de",    label: "Deutsch",            flag: "🇩🇪" },
  { code: "SV",    googleCode: "sv",    label: "Svenska",            flag: "🇸🇪" },
  { code: "PT",    googleCode: "pt",    label: "Português",          flag: "🇵🇹" },
  { code: "HU",    googleCode: "hu",    label: "Magyar",             flag: "🇭🇺" },
  { code: "PL",    googleCode: "pl",    label: "Polski",             flag: "🇵🇱" },
  { code: "NB",    googleCode: "no",    label: "Norsk bokmål",       flag: "🇳🇴" },
  { code: "PT-BR", googleCode: "pt",    label: "Português (Brasil)", flag: "🇧🇷" },
  { code: "NO",    googleCode: "no",    label: "Norsk",              flag: "🇳🇴" },
];

export default function LanguageDropdown() {
  const [active, setActive] = useState("EN");
  const [open, setOpen]     = useState(false);
  const ref                 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = [
      ".goog-te-banner-frame{display:none!important}",
      ".goog-te-balloon-frame{display:none!important}",
      "#goog-gt-tt{display:none!important}",
      "body{top:0!important}",
    ].join("");
    document.head.appendChild(style);

    window.googleTranslateElementInit = () => {
      new window.google!.translate.TranslateElement(
        { pageLanguage: "en", autoDisplay: false },
        "google_translate_element"
      );
    };

    if (!document.querySelector('script[src*="translate_a/element"]')) {
      const s = document.createElement("script");
      s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      s.async = true;
      document.head.appendChild(s);
    }

    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const triggerTranslate = (googleCode: string, attempt = 0) => {
    const select = document.querySelector(".goog-te-combo") as HTMLSelectElement | null;
    if (!select) {
      if (attempt < 15) setTimeout(() => triggerTranslate(googleCode, attempt + 1), 300);
      return;
    }
    const hasOption = Array.from(select.options).some((o) => o.value === googleCode);
    if (!hasOption) {
      console.warn(`[LanguageDropdown] Google Translate has no option "${googleCode}"`);
      return;
    }
    select.value = googleCode;
    select.dispatchEvent(new Event("change"));
  };

  const handleSelect = (lang: (typeof languages)[0]) => {
    setActive(lang.code);
    setOpen(false);
    if (lang.googleCode === "en") {
      const host = window.location.hostname;
      document.cookie = "googtrans=; max-age=0; path=/";
      document.cookie = `googtrans=; max-age=0; path=/; domain=.${host}`;
      window.location.reload();
    } else {
      triggerTranslate(lang.googleCode);
    }
  };

  const activeLang = languages.find((l) => l.code === active) ?? languages[0];

  return (
    <>
      <div id="google_translate_element" className="hidden" aria-hidden="true" />

      <div ref={ref} className="relative mt-6 pt-5 border-t border-foreground/10">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-foreground/15 text-sm font-medium hover:bg-foreground/5 transition-colors"
        >
          <span className="text-base leading-none notranslate" aria-hidden="true">{activeLang.flag}</span>
          <span className="flex-1 text-left notranslate">
            {activeLang.code} · {activeLang.label}
          </span>
          <svg
            className={`w-4 h-4 text-foreground/40 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute bottom-full left-0 mb-1 w-full bg-background border border-foreground/15 rounded-lg shadow-xl overflow-hidden z-50">
            <div className="max-h-60 overflow-y-auto py-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleSelect(lang)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors notranslate ${
                    lang.code === active
                      ? "bg-foreground/8 font-semibold text-foreground"
                      : "text-foreground/65 hover:bg-foreground/5 hover:text-foreground/90"
                  }`}
                >
                  <span className="text-base leading-none notranslate" aria-hidden="true">{lang.flag}</span>
                  <span className="w-10 shrink-0 font-medium">{lang.code}</span>
                  <span className="text-foreground/50 text-xs">{lang.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
